import React from 'react';
import { Box, Typography } from '@mui/material';
import { LAB_RESULT_ABNORMAL_MARKER, LAB_RESULT_COLUMN_HEADER } from '@oh-vcp/components-common';

const DATE_LINE = /^\d{4}-\d{2}-\d{2}$/;
const LAB_RESULT_LINE = /^(.+?),\s(.*?)\s\((.*)\)$/;

export type LabResultLine =
  | { kind: 'date'; text: string }
  | { kind: 'columnHeader'; text: string }
  | { kind: 'result'; name: string; value: string; range: string; abnormal: boolean }
  | { kind: 'text'; text: string };

export const parseLabResultLines = (content: string): LabResultLine[] =>
  content
    .split('\n')
    .map((rawLine) => rawLine.trim())
    .filter(Boolean)
    .map((line): LabResultLine => {
      if (DATE_LINE.test(line)) return { kind: 'date', text: line };
      if (line === LAB_RESULT_COLUMN_HEADER) return { kind: 'columnHeader', text: line };

      const abnormal = line.endsWith(` ${LAB_RESULT_ABNORMAL_MARKER}`);
      const withoutMarker = abnormal
        ? line.slice(0, -(LAB_RESULT_ABNORMAL_MARKER.length + 1)).trim()
        : line;
      const match = withoutMarker.match(LAB_RESULT_LINE);

      return match
        ? { kind: 'result', name: match[1], value: match[2], range: match[3], abnormal }
        : { kind: 'text', text: withoutMarker };
    });

const EconsultLabResultLines: React.FC<{ content: string }> = ({ content }) => (
  <>
    {parseLabResultLines(content).map((line, index) => {
      const key = `${index}`;

      if (line.kind === 'date') {
        return (
          <Typography
            key={key}
            variant="body2"
            color="base.grey3"
            fontWeight="bold"
            mt={index === 0 ? 0 : 2}>
            {line.text}
          </Typography>
        );
      }

      if (line.kind === 'columnHeader') {
        return (
          <Typography key={key} variant="body2" color="base.grey4">
            {line.text}
          </Typography>
        );
      }

      if (line.kind === 'result') {
        return (
          <Typography key={key} variant="body2" color="base.grey3">
            {`${line.name}, `}
            <Box component="span" color={line.abnormal ? 'error.main' : 'inherit'}>
              {line.value}
            </Box>
            {line.range ? ` (${line.range})` : ''}
          </Typography>
        );
      }

      return (
        <Typography key={key} variant="body2" color="base.grey3">
          {line.text}
        </Typography>
      );
    })}
  </>
);

export default EconsultLabResultLines;
